import { useMemo } from 'react';
import { useFetchCnGoodsCbam } from 'modules/Common/hooks/useFetchCnGoodsCbam';
import formatCnCode from 'modules/Common/utils/formatCnCode';

// Item 16B (fixpack 2026-07-23): string-only call sites (SelectField option
// labels, plain-text hints/summaries, gaps.js message text) can't render
// CgCnGoodHoverText's JSX hover card, but they still deserve a REAL label
// instead of the demo-era `cnCodeLabel`/`CN_CODE_OPTIONS` (mockData.js — a
// hardcoded 6-entry steel/aluminium list that silently fell back to the raw
// code for every CN code outside that list). This resolves the SAME CBAM
// catalog + flexible-matching rule CgCnGoodHoverText.jsx uses
// (useFetchCnGoodsCbam + formatCnCode's "7208.10" vs "7208.10.00" matching)
// into a plain "<formatted code> — <official name>" string.

// Pure and exported separately so it's unit-testable without mounting a
// component or mocking react-query (mirrors ShareFormPage's
// pure-mapping-function-plus-thin-hook convention elsewhere in this module).
export function resolveCnGoodLabel(cnGoodsById, code) {
  if (!code) return code;
  const formatted = formatCnCode(code) || String(code);
  const good = cnGoodsById?.get ? cnGoodsById.get(formatted) : undefined;
  return good?.name ? `${formatted} — ${good.name}` : formatted;
}

// Returns a stable `label(code)` function backed by whatever the CBAM
// catalog query currently has cached — a raw/blank catalog (still loading,
// or the code isn't a real CBAM good) falls back to the formatted code,
// never throws, and never blocks the caller on the fetch.
export default function useCnGoodLabel() {
  const { data: cnGoods } = useFetchCnGoodsCbam();

  const cnGoodsById = useMemo(() => {
    const map = new Map();
    (cnGoods || []).forEach((good) => {
      if (good?._id) map.set(good._id, good);
    });
    return map;
  }, [cnGoods]);

  return useMemo(() => (code) => resolveCnGoodLabel(cnGoodsById, code), [cnGoodsById]);
}
